import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { hasPermission } from '@/modules/auth/useAuth';
import { useBranchScope } from '@/modules/auth/useAuth';
import { PET_CLINICAL_SUMMARY_QUERY } from '@/modules/clinical-records/clinical.gql';
import { WeightChart } from '@/modules/clinical-records/WeightChart';
import { CustomerPetPicker } from '@/modules/doctor-pos/CustomerPetPicker';
import { TotemMark } from '@/shared/brand/TotemLogo';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

import {
  ADD_HOSP_NOTE_MUTATION,
  ADMIT_HOSPITALIZATION_MUTATION,
  CANCEL_HOSPITALIZATION_MUTATION,
  CHANGE_HOSP_LOCATION_MUTATION,
  DECEASED_HOSPITALIZATION_MUTATION,
  DISCHARGE_HOSPITALIZATION_MUTATION,
  HOSPITALIZATION_EPISODE_QUERY,
  HOSPITALIZATION_EPISODES_QUERY,
  HOSP_PROGRESS_NOTES_QUERY,
  HOSP_VITALS_QUERY,
  RECORD_HOSP_VITAL_MUTATION,
  REOPEN_HOSPITALIZATION_MUTATION,
  TRANSFER_HOSPITALIZATION_MUTATION,
  VOID_HOSP_VITAL_MUTATION,
  idempotencyContext,
} from './hospitalization.gql';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call */

const STATUSES = ['admitted', 'discharged', 'deceased', 'transferred', 'cancelled'] as const;
const DISCHARGE_TYPES = ['routine', 'against_medical_advice', 'referral', 'elective', 'other'] as const;
const TABS = ['evolution', 'vitals', 'charges', 'discharge'] as const;
type Tab = (typeof TABS)[number];

function errText(e: unknown): string {
  if (e instanceof ApolloError) return e.graphQLErrors[0]?.message ?? e.message;
  if (e instanceof Error) return e.message;
  return 'error';
}

function AdmitDialog({ onClose, onAdmitted }: { onClose: () => void; onAdmitted: (id: string) => void }): JSX.Element {
  const { t } = useTranslation();
  const [sel, setSel] = useState<{ customer_id: string | null; pet_id: string | null }>({ customer_id: null, pet_id: null });
  const [reason, setReason] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [admit, { loading }] = useMutation(ADMIT_HOSPITALIZATION_MUTATION);

  const onSubmit = async (): Promise<void> => {
    if (!sel.pet_id) return;
    setError(null);
    try {
      const res = await admit({
        variables: { input: { pet_id: sel.pet_id, admission_reason: reason.trim() || undefined, location_label: location.trim() || undefined } },
        context: idempotencyContext(),
      });
      onAdmitted(res.data?.admitHospitalization?.id as string);
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-background w-full max-w-lg space-y-3 rounded-lg border p-4 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-base font-bold tracking-tight">{t('hospitalization.admit_title')}</h2>
        {error ? <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">{error}</div> : null}
        <CustomerPetPicker customer_id={sel.customer_id} pet_id={sel.pet_id} onChange={setSel} />
        <div className="space-y-1">
          <Label>{t('hospitalization.admission_reason')}</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>{t('hospitalization.location')}</Label>
          <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t('hospitalization.location_ph')} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!sel.pet_id || loading} onClick={() => void onSubmit()}>{t('hospitalization.admit')}</Button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const { t } = useTranslation();
  const variant = status === 'admitted' ? 'success' : status === 'deceased' ? 'destructive' : 'muted';
  return <Badge variant={variant}>{t(`hospitalization.status_${status}`)}</Badge>;
}

function EvolutionTab({ episodeId, open, canWrite }: { episodeId: string; open: boolean; canWrite: boolean }): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch } = useQuery(HOSP_PROGRESS_NOTES_QUERY, { variables: { episode_id: episodeId }, fetchPolicy: 'cache-and-network' });
  const notes: any[] = data?.hospitalizationProgressNotes ?? [];
  const [body, setBody] = useState('');
  const [kind, setKind] = useState('evolution');
  const [addNote] = useMutation(ADD_HOSP_NOTE_MUTATION);

  const onAdd = async (): Promise<void> => {
    if (body.trim() === '') return;
    try {
      await addNote({ variables: { input: { episode_id: episodeId, note_kind: kind, body: body.trim() } }, context: idempotencyContext() });
      setBody('');
      await refetch();
      showToast({ tone: 'success', title: t('hospitalization.toast_note_added') });
    } catch (e) {
      showToast({ tone: 'error', title: errText(e) });
    }
  };

  return (
    <div className="space-y-2">
      {open && canWrite ? (
        <div className="space-y-1 border-b pb-2">
          <div className="flex gap-2">
            <select className="border-input bg-background h-9 rounded-md border px-2 text-sm" value={kind} onChange={(e) => setKind(e.target.value)}>
              {['evolution', 'treatment', 'nursing', 'other'].map((k) => <option key={k} value={k}>{t(`hospitalization.note_kind_${k}`)}</option>)}
            </select>
            <Button size="sm" onClick={() => void onAdd()} disabled={body.trim() === ''}>{t('hospitalization.add_note')}</Button>
          </div>
          <textarea className="border-input bg-background min-h-[60px] w-full rounded-md border px-3 py-2 text-sm" value={body} onChange={(e) => setBody(e.target.value)} placeholder={t('hospitalization.note_ph')} />
        </div>
      ) : null}
      {notes.length === 0 ? <p className="text-muted-foreground text-sm">{t('hospitalization.no_notes')}</p> : (
        <ul className="space-y-1">
          {notes.map((n) => (
            <li key={n.id} className={`rounded-md border px-2 py-1 text-sm ${n.voided_at ? 'opacity-50 line-through' : ''}`}>
              <p className="text-muted-foreground text-xs">{new Date(n.noted_at).toLocaleString()} · {t(`hospitalization.note_kind_${n.note_kind}`)}</p>
              <p>{n.body}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VitalsTab({ episodeId, open, canWrite }: { episodeId: string; open: boolean; canWrite: boolean }): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch } = useQuery(HOSP_VITALS_QUERY, { variables: { episode_id: episodeId }, fetchPolicy: 'cache-and-network' });
  const vitals: any[] = data?.hospitalizationVitalSigns ?? [];
  const [f, setF] = useState({ weight_grams: '', body_temperature_celsius_tenths: '', heart_rate_bpm: '', respiratory_rate_bpm: '', mucous_membranes: '' });
  const [record] = useMutation(RECORD_HOSP_VITAL_MUTATION);
  const [voidVital] = useMutation(VOID_HOSP_VITAL_MUTATION);

  const intOrU = (v: string): number | undefined => { const n = Number.parseInt(v, 10); return v.trim() === '' || Number.isNaN(n) ? undefined : n; };
  const tempTenths = (v: string): number | undefined => { const n = Number.parseFloat(v); return v.trim() === '' || Number.isNaN(n) ? undefined : Math.round(n * 10); };
  // Input is kilograms; the API stores grams.
  const weightGrams = (v: string): number | undefined => { const n = Number.parseFloat(v); return v.trim() === '' || Number.isNaN(n) ? undefined : Math.round(n * 1000); };

  const onRecord = async (): Promise<void> => {
    try {
      await record({ variables: { input: { episode_id: episodeId, weight_grams: weightGrams(f.weight_grams), body_temperature_celsius_tenths: tempTenths(f.body_temperature_celsius_tenths), heart_rate_bpm: intOrU(f.heart_rate_bpm), respiratory_rate_bpm: intOrU(f.respiratory_rate_bpm), mucous_membranes: f.mucous_membranes.trim() || undefined } }, context: idempotencyContext() });
      setF({ weight_grams: '', body_temperature_celsius_tenths: '', heart_rate_bpm: '', respiratory_rate_bpm: '', mucous_membranes: '' });
      await refetch();
      showToast({ tone: 'success', title: t('hospitalization.toast_vital_recorded') });
    } catch (e) {
      showToast({ tone: 'error', title: errText(e) });
    }
  };
  const onVoid = async (id: string): Promise<void> => {
    try {
      await voidVital({ variables: { input: { vital_id: id, reason: 'Corrección' } }, context: idempotencyContext() });
      await refetch();
    } catch (e) { showToast({ tone: 'error', title: errText(e) }); }
  };

  const weightPoints = vitals.filter((v) => v.weight_grams != null).map((v) => ({ measured_at: v.measured_at, weight_grams: v.weight_grams }));

  return (
    <div className="space-y-2">
      {weightPoints.length >= 2 ? <WeightChart points={weightPoints} /> : null}
      {open && canWrite ? (
        <div className="grid grid-cols-3 gap-2 border-b pb-2">
          <Input placeholder={t('hospitalization.v_weight_kg')} value={f.weight_grams} onChange={(e) => setF({ ...f, weight_grams: e.target.value })} type="number" />
          <Input placeholder={t('hospitalization.v_temp_c')} value={f.body_temperature_celsius_tenths} onChange={(e) => setF({ ...f, body_temperature_celsius_tenths: e.target.value })} type="number" step="0.1" />
          <Input placeholder={t('hospitalization.v_hr')} value={f.heart_rate_bpm} onChange={(e) => setF({ ...f, heart_rate_bpm: e.target.value })} type="number" />
          <Input placeholder={t('hospitalization.v_rr')} value={f.respiratory_rate_bpm} onChange={(e) => setF({ ...f, respiratory_rate_bpm: e.target.value })} type="number" />
          <Input placeholder={t('hospitalization.v_mucous')} value={f.mucous_membranes} onChange={(e) => setF({ ...f, mucous_membranes: e.target.value })} />
          <Button size="sm" onClick={() => void onRecord()}>{t('hospitalization.record_vital')}</Button>
        </div>
      ) : null}
      {vitals.length === 0 ? <p className="text-muted-foreground text-sm">{t('hospitalization.no_vitals')}</p> : (
        <table className="w-full text-xs">
          <thead className="text-muted-foreground text-left">
            <tr><th className="pr-2">{t('hospitalization.th_time')}</th><th className="pr-2">kg</th><th className="pr-2">°C</th><th className="pr-2">FC</th><th className="pr-2">FR</th><th></th></tr>
          </thead>
          <tbody>
            {vitals.map((v) => (
              <tr key={v.id} className="border-t">
                <td className="pr-2 tabular-nums">{new Date(v.measured_at).toLocaleString()}</td>
                <td className="pr-2 tabular-nums">{v.weight_grams != null ? (v.weight_grams / 1000).toFixed(2) : '—'}</td>
                <td className="pr-2 tabular-nums">{v.body_temperature_celsius_tenths != null ? (v.body_temperature_celsius_tenths / 10).toFixed(1) : '—'}</td>
                <td className="pr-2 tabular-nums">{v.heart_rate_bpm ?? '—'}</td>
                <td className="pr-2 tabular-nums">{v.respiratory_rate_bpm ?? '—'}</td>
                <td>{open && canWrite ? <button className="text-destructive text-xs" onClick={() => void onVoid(v.id)}>{t('hospitalization.void')}</button> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DischargeTab({ ep, canDischarge, isAdmin, onChanged }: { ep: any; canDischarge: boolean; isAdmin: boolean; onChanged: () => Promise<unknown> }): JSX.Element {
  const { t } = useTranslation();
  const [dtype, setDtype] = useState('routine');
  const [summary, setSummary] = useState('');
  const [discharge] = useMutation(DISCHARGE_HOSPITALIZATION_MUTATION);
  const [deceased] = useMutation(DECEASED_HOSPITALIZATION_MUTATION);
  const [transfer] = useMutation(TRANSFER_HOSPITALIZATION_MUTATION);
  const [cancel] = useMutation(CANCEL_HOSPITALIZATION_MUTATION);
  const [reopen] = useMutation(REOPEN_HOSPITALIZATION_MUTATION);

  const run = async (fn: () => Promise<unknown>, msg: string): Promise<void> => {
    try { await fn(); await onChanged(); showToast({ tone: 'success', title: msg }); }
    catch (e) { showToast({ tone: 'error', title: errText(e) }); }
  };

  if (ep.status !== 'admitted') {
    return (
      <div className="space-y-2 text-sm">
        <p>{t('hospitalization.already_closed', { status: t(`hospitalization.status_${ep.status}`) })}</p>
        {ep.discharge_summary ? <p className="text-muted-foreground">{ep.discharge_summary}</p> : null}
        {isAdmin && ep.status === 'discharged' ? (
          <Button size="sm" variant="outline" onClick={() => void run(() => reopen({ variables: { episode_id: ep.id }, context: idempotencyContext() }), t('hospitalization.toast_reopened'))}>{t('hospitalization.reopen')}</Button>
        ) : null}
      </div>
    );
  }
  if (!canDischarge) return <p className="text-muted-foreground text-sm">{t('hospitalization.no_discharge_perm')}</p>;

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label>{t('hospitalization.discharge_type')}</Label>
        <select className="border-input bg-background h-9 w-full rounded-md border px-2 text-sm" value={dtype} onChange={(e) => setDtype(e.target.value)}>
          {DISCHARGE_TYPES.map((d) => <option key={d} value={d}>{t(`hospitalization.discharge_type_${d}`)}</option>)}
        </select>
      </div>
      <div className="space-y-1">
        <Label>{t('hospitalization.discharge_summary')}</Label>
        <textarea className="border-input bg-background min-h-[60px] w-full rounded-md border px-3 py-2 text-sm" value={summary} onChange={(e) => setSummary(e.target.value)} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => void run(() => discharge({ variables: { input: { episode_id: ep.id, discharge_type: dtype, discharge_summary: summary.trim() || undefined } }, context: idempotencyContext() }), t('hospitalization.toast_discharged'))}>{t('hospitalization.discharge')}</Button>
        <Button size="sm" variant="outline" onClick={() => void run(() => deceased({ variables: { input: { episode_id: ep.id, discharge_summary: summary.trim() || undefined } }, context: idempotencyContext() }), t('hospitalization.toast_deceased'))}>{t('hospitalization.mark_deceased')}</Button>
        <Button size="sm" variant="outline" onClick={() => void run(() => transfer({ variables: { input: { episode_id: ep.id, transfer_destination: summary.trim() || 'Traslado', discharge_summary: summary.trim() || undefined } }, context: idempotencyContext() }), t('hospitalization.toast_transferred'))}>{t('hospitalization.transfer')}</Button>
        <Button size="sm" variant="ghost" onClick={() => void run(() => cancel({ variables: { input: { episode_id: ep.id, reason: summary.trim() || 'Cancelado' } }, context: idempotencyContext() }), t('hospitalization.toast_cancelled'))}>{t('hospitalization.cancel_episode')}</Button>
      </div>
    </div>
  );
}

function EpisodeDetail({ episodeId, onChanged }: { episodeId: string; onChanged: () => Promise<unknown> }): JSX.Element | null {
  const { t } = useTranslation();
  const perms = useAuthStore((s) => s.user?.permissions ?? []);
  const roles = useAuthStore((s) => s.user?.roles ?? []);
  const isAdmin = roles.includes('administrator');
  const canWrite = hasPermission(perms, 'hospitalization.write');
  const canVitals = hasPermission(perms, 'hospitalization.vitals.write');
  const canDischarge = hasPermission(perms, 'hospitalization.discharge');

  const { data, refetch } = useQuery(HOSPITALIZATION_EPISODE_QUERY, { variables: { id: episodeId }, fetchPolicy: 'cache-and-network' });
  const ep: any = data?.hospitalizationEpisode ?? null;
  const { data: petData } = useQuery(PET_CLINICAL_SUMMARY_QUERY, { variables: { pet_id: ep?.pet_id ?? '' }, skip: !ep?.pet_id, fetchPolicy: 'cache-and-network' });
  const pet = petData?.petClinicalSummary ?? null;
  const [tab, setTab] = useState<Tab>('evolution');
  const [changeLocation] = useMutation(CHANGE_HOSP_LOCATION_MUTATION);

  if (!ep) return <p className="text-muted-foreground p-4 text-sm">{t('hospitalization.pick')}</p>;
  const open = ep.status === 'admitted';
  const refresh = async (): Promise<void> => { await refetch(); await onChanged(); };

  const onLoc = async (): Promise<void> => {
    const next = window.prompt(t('hospitalization.location'), ep.location_label ?? '');
    if (next == null || next.trim() === '') return;
    try { await changeLocation({ variables: { input: { episode_id: ep.id, location_label: next.trim() } }, context: idempotencyContext() }); await refresh(); }
    catch (e) { showToast({ tone: 'error', title: errText(e) }); }
  };

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-display font-bold tracking-tight">{ep.folio_number} · {pet?.pet_name ?? '—'}{pet?.species ? ` (${pet.species})` : ''}</p>
          <p className="text-muted-foreground text-xs">{t('hospitalization.location')}: {ep.location_label ?? '—'} {open && canWrite ? <button className="underline" onClick={() => void onLoc()}>{t('hospitalization.change')}</button> : null}</p>
        </div>
        <StatusBadge status={ep.status} />
      </div>

      <div className="flex gap-1 border-b text-sm">
        {TABS.map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} className={`px-3 py-1.5 ${tab === tb ? 'border-primary border-b-2 font-medium' : 'text-muted-foreground'}`}>{t(`hospitalization.tab_${tb}`)}</button>
        ))}
      </div>

      {tab === 'evolution' ? <EvolutionTab episodeId={ep.id} open={open} canWrite={canWrite} /> : null}
      {tab === 'vitals' ? <VitalsTab episodeId={ep.id} open={open} canWrite={canVitals} /> : null}
      {tab === 'charges' ? (
        <div className="space-y-2 text-sm">
          <p>{t('hospitalization.charges_account')}: {ep.open_account_id ? <Link className="text-primary underline" to="/app/open-accounts">{t('hospitalization.view_account')}</Link> : '—'}</p>
          <p className="text-muted-foreground text-xs">{t('hospitalization.charges_hint')}</p>
        </div>
      ) : null}
      {tab === 'discharge' ? <DischargeTab ep={ep} canDischarge={canDischarge} isAdmin={isAdmin} onChanged={refresh} /> : null}
    </div>
  );
}

export function HospitalizationPage(): JSX.Element {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>('admitted');
  // Deep-link support: /app/hospitalization?episode=<id> pre-selects that episode (EpisodeDetail
  // loads it by id even when it's discharged and not in the current board list).
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState<string | null>(searchParams.get('episode'));
  const [showAdmit, setShowAdmit] = useState(false);
  const perms = useAuthStore((s) => s.user?.permissions ?? []);
  const canWrite = hasPermission(perms, 'hospitalization.write');
  const branch_id = useBranchScope();

  const { data, refetch } = useQuery(HOSPITALIZATION_EPISODES_QUERY, {
    variables: { status: status || null, branch_id },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  });
  const episodes: any[] = data?.hospitalizationEpisodes ?? [];

  return (
    <div className="grid h-full grid-cols-[300px_1fr] gap-0">
      <div className="space-y-2 overflow-y-auto border-r p-3">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-lg font-bold tracking-tight">{t('hospitalization.title')}</h1>
          {canWrite ? <Button size="sm" onClick={() => setShowAdmit(true)}>{t('hospitalization.admit')}</Button> : null}
        </div>
        <select className="border-input bg-background h-9 w-full rounded-md border px-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`hospitalization.status_${s}`)}</option>)}
        </select>
        {episodes.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-4 text-center">
            <TotemMark className="h-10 w-auto text-muted-foreground/40" aria-hidden />
            <p className="text-muted-foreground text-sm">{t('hospitalization.empty')}</p>
          </div>
        ) : (
          <ul className="space-y-1">
            {episodes.map((e) => (
              <li key={e.id}>
                <button onClick={() => setSelected(e.id)} className={`w-full rounded-md border p-2 text-left text-sm ${selected === e.id ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}>
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-medium">{e.folio_number}</span>
                    <StatusBadge status={e.status} />
                  </div>
                  <span className="text-muted-foreground text-xs">{e.location_label ?? '—'} · {new Date(e.admitted_at).toLocaleDateString()}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="overflow-y-auto">
        {selected ? <EpisodeDetail episodeId={selected} onChanged={refetch} /> : (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
            <TotemMark className="h-10 w-auto text-muted-foreground/40" aria-hidden />
            <p className="text-muted-foreground text-sm">{t('hospitalization.pick')}</p>
          </div>
        )}
      </div>

      {showAdmit ? <AdmitDialog onClose={() => setShowAdmit(false)} onAdmitted={(id) => { setShowAdmit(false); setSelected(id); setStatus('admitted'); void refetch(); }} /> : null}
    </div>
  );
}
